import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import SelectNew from "components/select/SelectNew";
import { IBookingCategory } from "redux/reducers/models/teetime";

const MAX_VISIBLE_TAGS = 6;
const EMPTY_SELECT_VALUE = -1;

interface IBookingCategoryTagsSelectProps {
  bookingCategories: Array<IBookingCategory>;
  selectedIds: Array<number>;
  onChange: (selectedIds: Array<number>) => void;
}

export default function BookingCategoryTagsSelect(props: IBookingCategoryTagsSelectProps) {
  const { bookingCategories, selectedIds, onChange } = props;
  const { t } = useTranslation();
  const [selectKey, setSelectKey] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (selectedIds.length <= MAX_VISIBLE_TAGS) {
      setExpanded(false);
    }
  }, [selectedIds.length]);

  const selectedCategories = selectedIds
    .map(id => bookingCategories?.find(category => category.id === id))
    .filter((category): category is IBookingCategory => !!category);
  const availableCategories =
    bookingCategories?.filter(category => !selectedIds.includes(category.id)) ?? [];
  const visibleCategories = expanded
    ? selectedCategories
    : selectedCategories.slice(0, MAX_VISIBLE_TAGS);
  const hasOverflow = selectedCategories.length > MAX_VISIBLE_TAGS;
  const hiddenCount = selectedCategories.length - MAX_VISIBLE_TAGS;

  function handleAdd(value: number) {
    if (isNaN(value) || value === EMPTY_SELECT_VALUE || selectedIds.includes(value)) {
      return;
    }
    onChange([...selectedIds, value]);
    setSelectKey(prev => prev + 1);
  }

  function handleRemove(value: number) {
    onChange(selectedIds.filter(id => id !== value));
  }

  return (
    <div>
      <SelectNew
        key={selectKey}
        id="booking_categories"
        label={t("Booking categories")}
        value={EMPTY_SELECT_VALUE}
        options={availableCategories.map(bookingCategory => ({
          id: bookingCategory.id,
          label: bookingCategory.title,
        }))}
        onChange={value => handleAdd(value)}
        placeholder={t("Select booking categories")}
      />
      {selectedCategories.length > 0 ? (
        <div className="ui-teesheet-template-tags-container">
          {visibleCategories.map(category => (
            <div key={category.id} className="ui-teesheet-template-tag">
              <p className="ui-teesheet-template-tag-text">{category.title}</p>
              <FontAwesomeIcon
                icon={faXmark}
                className="ui-teesheet-template-tag-x"
                onClick={() => handleRemove(category.id)}
              />
            </div>
          ))}
          {hasOverflow ? (
            <button
              type="button"
              className="ui-teesheet-template-tags-toggle"
              onClick={() => setExpanded(prev => !prev)}
            >
              {expanded ? t("Show less") : `+${hiddenCount} ${t("more")}`}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
